import { useState } from "react";
import { toast } from "sonner";
import { Moon, Sunrise, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const FEEL = [["great", "Good day"], ["okay", "Okay day"], ["hard", "Hard day"]];
const fmtTime = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return `${h % 12 || 12}:${String(m).padStart(2, "0")}${h < 12 ? "am" : "pm"}`; };

export function BoundaryCard({ b, clock, onDone }) {
  const [busy, setBusy] = useState(false);
  const choose = async (choice) => {
    setBusy(true);
    try { await api.post("/v2/day/boundary", { choice }); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="card p-6 rise" data-testid="boundary-card">
      <span className="eyebrow flex items-center gap-1.5"><Moon className="h-3.5 w-3.5" />It's {fmtTime(clock.time)}</span>
      <p className="font-display text-2xl mt-2 leading-snug">Still {b.stay_weekday}, or starting {b.start_weekday}?</p>
      <p className="text-sm text-stone-600 mt-1">You were here a little while ago. Pick whichever feels true. Nothing is lost either way.</p>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <button disabled={busy} onClick={() => choose("stay")} data-testid="boundary-stay-btn" className="h-12 rounded-full border border-stone-300 text-sm font-semibold disabled:opacity-50">Still {b.stay_weekday}</button>
        <button disabled={busy} onClick={() => choose("start")} data-testid="boundary-start-btn" className="btn-ink h-12 rounded-full text-sm font-semibold disabled:opacity-50">Start {b.start_weekday}</button>
      </div>
    </div>
  );
}

export function YesterdayWrap({ date, onDone }) {
  const [busy, setBusy] = useState(false);
  const send = async (feeling) => {
    setBusy(true);
    try { await api.post("/v2/today/wrap", { feeling, date }); if (feeling !== "skip") toast.success("Saved to yesterday."); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="card p-5 bg-[#F5F2EC] rise" data-testid="yesterday-wrap">
      <div className="flex items-start justify-between gap-2">
        <span className="eyebrow flex items-center gap-1.5"><Sunrise className="h-3.5 w-3.5" />Optional</span>
        <button disabled={busy} onClick={() => send("skip")} data-testid="yesterday-wrap-skip" className="tap text-xs text-stone-500 underline">Skip</button>
      </div>
      <p className="font-display text-lg mt-1">How did yesterday end?</p>
      <div className="grid grid-cols-3 gap-2 mt-3">
        {FEEL.map(([k, l]) => (
          <button key={k} disabled={busy} onClick={() => send(k)} data-testid={`yesterday-wrap-${k}`}
            className="chip h-11 rounded-full text-sm font-medium border border-stone-200 bg-white hover:border-stone-400 disabled:opacity-50">{l}</button>
        ))}
      </div>
    </div>
  );
}

export function CarryCard({ step, onDay }) {
  const [busy, setBusy] = useState(false);
  const pick = async (keep) => {
    setBusy(true);
    try { const { data } = await api.post("/v2/today/carry", { keep }); onDay(data.day); if (keep) toast.success("Kept. It's today's step now."); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="card p-5 rise" data-testid="carry-card">
      <span className="eyebrow flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" />From yesterday</span>
      <p className="text-sm text-stone-600 mt-2">Yesterday's step is still here if you want it:</p>
      <p className="font-display text-lg mt-1 leading-snug" data-testid="carry-step-text">{step.text}</p>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <button disabled={busy} onClick={() => pick(true)} data-testid="carry-keep-btn" className="h-11 rounded-full border border-stone-300 text-sm font-semibold disabled:opacity-50">Keep it</button>
        <button disabled={busy} onClick={() => pick(false)} data-testid="carry-fresh-btn" className="btn-ink h-11 rounded-full text-sm font-semibold disabled:opacity-50">Fresh one</button>
      </div>
    </div>
  );
}
